import { useMemo } from 'react';
import { Button, Popconfirm, Select, Tabs, Tag, Typography } from 'antd';
import { CloseOutlined, DeleteOutlined, LockOutlined } from '@ant-design/icons';
import { LIFECYCLE_STAGES, nodeKindMeta, type LifecycleStage } from '@/theme/tokens';
import type { FlowNode } from '@/flow-ir/types';
import type { UiDiagnostic } from '@/flow-ir/diagnostics';
import { resolveGlyph } from './glyphs';
import { SchemaForm } from './SchemaForm';
import { capabilityFamily, familyForNode } from '@/edu/useFieldHelp';
import { DATA_TOUR } from '@/edu/tourModel';
import {
  coreFieldsForKind,
  GOVERNANCE_FIELDS,
  IDEMPOTENCY_FIELDS,
  type FieldSchema,
} from './schema';

const { Text } = Typography;

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontSize: 10.5,
        fontWeight: 700,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: 'var(--ol-text-tertiary)',
        margin: '14px 0 8px',
      }}
    >
      {children}
    </div>
  );
}

export interface NodeInspectorProps {
  node: FlowNode;
  editable: boolean;
  diagnostics?: UiDiagnostic[];
  /** All node ids (for confidenceGate.source selection). */
  nodeIds?: string[];
  /** llmTask node ids (for confidenceGate.source). */
  llmTaskIds?: string[];
  /** Optional provider config schema fields for a capability node. */
  capabilityConfigFields?: FieldSchema[];
  onChange?: (next: FlowNode) => void;
  onDelete?: () => void;
  onFocusDiagnostic?: (d: UiDiagnostic) => void;
  onClose: () => void;
}

/**
 * Node configuration side panel (spec §6). Editable in edit mode (schema-driven forms, rename,
 * delete, stage reassignment, status-set editing lives on the edge inspector); read-only (disabled
 * inputs) on the sample path. Never a modal — the graph stays visible during every edit.
 */
export function NodeInspector({
  node,
  editable,
  diagnostics = [],
  nodeIds = [],
  llmTaskIds = [],
  capabilityConfigFields,
  onChange,
  onDelete,
  onFocusDiagnostic,
  onClose,
}: NodeInspectorProps) {
  const meta = nodeKindMeta(node.kind);
  const emit = (next: Record<string, unknown>) => onChange?.(next as unknown as FlowNode);

  // Field-help families (F-ED-03): the capability family for capability nodes (else the node kind)
  // drives what/why/example on structured fields; the capability config block uses the bare family.
  const helpFamily = familyForNode(node);
  const capFamily = capabilityFamily(node);

  // confidenceGate.source is nicer as a select of upstream llmTask ids.
  const coreFields = useMemo<FieldSchema[]>(() => {
    const base = coreFieldsForKind(node.kind);
    if (node.kind === 'confidenceGate' && llmTaskIds.length > 0) {
      return base.map((f) =>
        f.key === 'source' ? { ...f, type: 'enum', enumValues: llmTaskIds } : f,
      );
    }
    return base;
  }, [node.kind, llmTaskIds]);

  const configFields = useMemo<FieldSchema[]>(() => {
    if (node.kind !== 'capability') return [];
    if (capabilityConfigFields && capabilityConfigFields.length > 0) return capabilityConfigFields;
    // No provider schema available — a single generic JSON editor for the whole config object.
    return [{ key: 'config', label: 'Config', type: 'json', description: 'Capability tuning payload (validated by the adapter schema).' }];
  }, [node.kind, capabilityConfigFields]);

  const configTab = (
    <div>
      <SectionTitle>Placement</SectionTitle>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div>
          <label style={{ fontSize: 11.5, color: 'var(--ol-text-secondary)', fontWeight: 600 }}>
            Lifecycle stage (lane)
          </label>
          <Select
            size="small"
            style={{ width: '100%', marginTop: 3 }}
            disabled={!editable}
            value={node.stage}
            onChange={(v: LifecycleStage) => emit({ ...node, stage: v })}
            options={LIFECYCLE_STAGES.map((s) => ({ value: s.key, label: s.label }))}
          />
        </div>
      </div>

      <SectionTitle>Configuration</SectionTitle>
      <SchemaForm
        fields={coreFields}
        target={node as unknown as Record<string, unknown>}
        onChange={emit}
        configurable={node.configurable}
        disabled={!editable}
        helpFamily={helpFamily}
      />

      {node.kind === 'capability' && (
        <>
          <SectionTitle>Capability config</SectionTitle>
          <SchemaForm
            fields={configFields}
            target={node as unknown as Record<string, unknown>}
            onChange={emit}
            configurable={node.configurable}
            disabled={!editable}
            helpFamily={capFamily}
          />
          <div style={{ marginTop: 10 }}>
            <SchemaForm
              fields={IDEMPOTENCY_FIELDS}
              target={node as unknown as Record<string, unknown>}
              onChange={emit}
              disabled={!editable}
              helpFamily="idempotency"
            />
          </div>
        </>
      )}

      <SectionTitle>Governance</SectionTitle>
      <SchemaForm
        fields={GOVERNANCE_FIELDS}
        target={node as unknown as Record<string, unknown>}
        onChange={emit}
        disabled={!editable}
        helpFamily="governance"
      />
      {node.locked && (
        <div style={{ marginTop: 8 }}>
          <Tag icon={<LockOutlined />} bordered={false}>
            @locked — variant children cannot override or remove this node
          </Tag>
        </div>
      )}
      {node.entityBinding !== undefined && (
        <div style={{ marginTop: 8 }}>
          <label style={{ fontSize: 11.5, color: 'var(--ol-text-secondary)', fontWeight: 600 }}>
            Entity role binding
          </label>
          <Select
            size="small"
            mode="tags"
            maxCount={1}
            style={{ width: '100%', marginTop: 3 }}
            disabled={!editable}
            value={node.entityBinding ? [node.entityBinding] : []}
            placeholder="subject (default)"
            onChange={(v) => emit({ ...node, entityBinding: (v as string[])[0] || undefined })}
          />
        </div>
      )}
    </div>
  );

  const issuesTab = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {diagnostics.length === 0 && (
        <Text type="secondary" style={{ fontSize: 12 }}>
          No compiler findings on this node.
        </Text>
      )}
      {diagnostics.map((d, i) => (
        <div
          key={i}
          className="ol-problem"
          role="button"
          tabIndex={0}
          onClick={() => onFocusDiagnostic?.(d)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') onFocusDiagnostic?.(d);
          }}
        >
          <span
            style={{
              color: `var(--ol-${d.severity === 'error' ? 'negative' : d.severity === 'warning' ? 'attention' : 'info'})`,
            }}
          >
            {d.severity === 'error' ? '●' : d.severity === 'warning' ? '▲' : 'ℹ'}
          </span>
          <span>
            <span className="ol-problem__code">{d.code}</span> {d.message}
          </span>
        </div>
      ))}
    </div>
  );

  return (
    <aside
      aria-label={`Inspector: ${node.name ?? node.id}`}
      data-tour={DATA_TOUR.inspector}
      style={{
        width: 360,
        flex: 'none',
        height: '100%',
        borderInlineStart: '1px solid var(--ol-border)',
        background: 'var(--ol-bg-elevated)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          padding: '12px 14px',
          borderBottom: '1px solid var(--ol-border)',
          display: 'flex',
          gap: 8,
          alignItems: 'flex-start',
        }}
      >
        <div style={{ minWidth: 0, flex: 1 }}>
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 5,
              fontSize: 10.5,
              fontWeight: 700,
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
              color: meta.accent,
            }}
          >
            {resolveGlyph(meta.glyph)}
            {meta.label}
            {meta.ai && (
              <Tag color="magenta" bordered={false} style={{ fontSize: 9, marginInlineStart: 4 }}>
                AI
              </Tag>
            )}
          </div>
          <div style={{ fontWeight: 600, fontSize: 15, marginTop: 3, lineHeight: 1.3 }}>
            {node.name ?? node.id}
          </div>
          <Text className="ol-mono" style={{ fontSize: 11, color: 'var(--ol-text-tertiary)' }}>
            {node.id}
          </Text>
        </div>
        {editable && onDelete && (
          <Popconfirm
            title="Delete this node?"
            description="Connected edges are removed too. Undoable."
            okText="Delete"
            okButtonProps={{ danger: true }}
            onConfirm={onDelete}
          >
            <Button type="text" size="small" danger aria-label="Delete node" icon={<DeleteOutlined />} />
          </Popconfirm>
        )}
        <Button type="text" size="small" aria-label="Close inspector" icon={<CloseOutlined />} onClick={onClose} />
      </div>

      <div style={{ overflow: 'auto', padding: '0 14px 20px' }}>
        <Tabs
          size="small"
          items={[
            { key: 'config', label: 'Config', children: configTab },
            {
              key: 'issues',
              label: `Issues${diagnostics.length ? ` · ${diagnostics.length}` : ''}`,
              children: issuesTab,
            },
          ]}
        />
        {!editable && (
          <Text type="secondary" style={{ fontSize: 11 }}>
            Read-only sample. Open a flow from the registry to edit.
          </Text>
        )}
        {nodeIds.length > 0 && node.kind === 'confidenceGate' && llmTaskIds.length === 0 && (
          <Text type="secondary" style={{ fontSize: 11 }}>
            Add an LLM task upstream to gate its confidence.
          </Text>
        )}
      </div>
    </aside>
  );
}
